import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { edgeErrorMessage } from "@/lib/edge-errors";
import type { MeetingActionItem, MeetingDecision } from "@/lib/db-types";

interface SummaryContext {
  meetingId: string;
  customerId: string | null;
}

async function invalidateSummary(qc: ReturnType<typeof useQueryClient>, ctx: SummaryContext) {
  await invalidateFor(qc, "meeting_summaries", { meetingId: ctx.meetingId });
  await invalidateFor(qc, "crm_meetings", { customerId: ctx.customerId ?? undefined });
  await invalidateFor(qc, "follow_ups", { customerId: ctx.customerId ?? undefined });
  await invalidateFor(qc, "customer_activities", { customerId: ctx.customerId ?? undefined });
}

/** Godkjenner referatet (RPC approve_meeting_summary): kontaktlogg, oppfølginger og lukking av godkjenningskortet. */
export function useApproveMeetingSummary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SummaryContext & { summaryId: string; summary: string; decisions: MeetingDecision[]; actionItems: MeetingActionItem[] }) => {
      const { error } = await supabase.rpc("approve_meeting_summary", {
        p_summary_id: input.summaryId,
        p_summary: input.summary,
        p_decisions: input.decisions,
        p_action_items: input.actionItems,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: async (_d, input) => {
      await invalidateSummary(qc, input);
      toast.success("Referatet er godkjent");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDiscardMeetingSummary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SummaryContext & { summaryId: string; reason: string }) => {
      const { error } = await supabase.rpc("discard_meeting_summary", { p_summary_id: input.summaryId, p_reason: input.reason });
      if (error) throw new Error(error.message);
    },
    onSuccess: async (_d, input) => {
      await invalidateSummary(qc, input);
      toast.success("Referatet er forkastet");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

/** Lager referat fra transkripsjonen, eller fra notater (fysiske møter og regenerering). */
export function useGenerateMeetingSummary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SummaryContext & { notes?: string }): Promise<boolean> => {
      const { data, error } = await supabase.functions.invoke<{ summary_id?: string; skipped?: string; error?: string }>("generate-meeting-summary", {
        body: { meeting_id: input.meetingId, notes: input.notes },
      });
      if (error) throw new Error(await edgeErrorMessage(error, data, "Kunne ikke lage referatet"));
      if (data?.skipped === "agents_disabled" || data?.skipped === "no_api_key") throw new Error("Referatagenten er ikke skrudd på i dette miljøet");
      if (data?.skipped === "flag_off") throw new Error("Møter er ikke skrudd på ennå");
      if (data?.error) throw new Error(data.error);
      return !!data?.summary_id;
    },
    onSuccess: async (_d, input) => {
      await invalidateSummary(qc, input);
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
